import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Menu, X, Globe } from "lucide-react";
import { useLanguage, LANGUAGES } from "@/i18n/LanguageContext";
import { Emblem } from "./Emblem";
import { NAV_LINKS } from "./data";

const LangSwitcher = ({ mobile = false }) => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid={mobile ? "language-switcher-mobile" : "language-switcher-button"}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-2.5 text-[12px] font-semibold uppercase tracking-[0.15em] text-slate-300 transition-colors duration-300 hover:text-gold"
      >
        <Globe className="h-3.5 w-3.5" /> {lang}
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
            <motion.div
              data-testid="language-menu"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 z-20 mt-1 w-44 border border-navy-border bg-navy-deep shadow-2xl"
            >
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  data-testid={`language-option-${l.code}`}
                  onClick={() => { setLang(l.code); setOpen(false); }}
                  className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors duration-200 hover:bg-navy-card hover:text-gold ${
                    lang === l.code ? "text-gold" : "text-slate-300"
                  }`}
                >
                  {l.label}
                  <span className="font-mono text-[10px] uppercase text-slate-500">{l.code}</span>
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = ({ onInitiate, onPortal }) => {
  const { t } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50 border-b border-gold/15 bg-navy/85 shadow-2xl backdrop-blur-xl"
    >
      <div className="mx-auto flex min-h-[84px] max-w-7xl items-center justify-between gap-3 px-4 py-2.5 sm:min-h-[72px] sm:px-6 sm:py-0 lg:px-8">
        <a href="#top" data-testid="brand-lockup" className="group flex min-w-0 items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center text-gold transition-transform duration-500 group-hover:scale-105">
            <Emblem className="h-10 w-9" />
          </span>
          <span className="min-w-0 leading-tight">
            <span className="block font-serif text-xl font-semibold tracking-wide text-slate-100">
              LEXAEGIS <span className="font-mono text-[10px] font-normal uppercase tracking-[0.35em] text-gold">International</span>
            </span>
            <span
              data-testid="header-firm-signature"
              className="mt-1 block text-[11px] font-medium leading-snug tracking-[0.06em] text-gold"
              style={{ WebkitTextStroke: "0.4px #A31621", paintOrder: "stroke fill", whiteSpace: "normal" }}
            >
              {t.brandSub}
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-5 xl:flex" data-testid="desktop-nav">
          {NAV_LINKS.map((l, i) => (
            <a key={l.href} href={l.href} data-testid={l.testid}
              className="text-[12px] font-medium tracking-wide text-slate-300 transition-colors duration-300 hover:text-gold">
              {t.nav[i]}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-1 xl:flex">
          <LangSwitcher />
          <button onClick={onPortal} data-testid="header-portal-login-button"
            className="inline-flex items-center gap-2 px-3 py-2.5 text-[12px] font-semibold uppercase tracking-[0.15em] text-slate-300 transition-colors duration-300 hover:text-gold">
            <Lock className="h-3.5 w-3.5" /> {t.portalLabel}
          </button>
          <button onClick={onInitiate} data-testid="header-initiate-cta"
            className="border border-gold/60 bg-gold/10 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-gold transition-colors duration-300 hover:bg-gold hover:text-navy-deep">
            {t.initiate}
          </button>
        </div>

        <button className="text-slate-200 xl:hidden" data-testid="mobile-menu-button"
          onClick={() => setMobileOpen((v) => !v)} aria-label="Toggle menu">
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-gold/10 bg-navy-deep/95 backdrop-blur-xl xl:hidden" data-testid="mobile-nav">
          <div className="flex flex-col gap-1 px-6 py-4">
            {NAV_LINKS.map((l, i) => (
              <a key={l.href} href={l.href} data-testid={`${l.testid}-mobile`}
                onClick={() => setMobileOpen(false)}
                className="py-2.5 text-sm text-slate-300 transition-colors hover:text-gold">
                {t.nav[i]}
              </a>
            ))}
            <div className="mt-2 flex items-center justify-between gap-3 border-t border-navy-border pt-3">
              <LangSwitcher mobile />
              <button onClick={() => { setMobileOpen(false); onInitiate(); }} data-testid="mobile-initiate-cta"
                className="border border-gold/60 px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-gold">
                {t.initiate}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.header>
  );
};
